import { useCallback, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { Checkbox } from "@lattice-php/form/components/checkbox/checkbox";
import { Renderer } from "@lattice-php/core/renderer";
import type { Node, Option } from "@lattice-php/core/types";
import {
  FieldCommitOverrideProvider,
  FormProvider,
  FormValuesProvider,
  getPath,
  PrefillProvider,
  ResolvedNodesProvider,
  setPath,
  TableCellProvider,
  useFormValues,
  useSetFormValue,
} from "@lattice-php/form/embed";
import { IconButton } from "@lattice-php/ui/primitives/icon-button";
import { useT } from "@lattice-php/ui/i18n";
import { cn } from "@lattice-php/ui/lib/utils";
import { isTruthy } from "@lattice-php/ui/lib/is-truthy";
import { filterValue, isActiveFilterValue } from "@lattice-php/table/lib/filter-values";
import type { FilterNode } from "@lattice-php/table/types";

export type FilterOptionSearch = (
  field: string,
  query: string,
  signal: AbortSignal,
) => Promise<Option[]>;

type FilterValue = Record<string, unknown>;

export function TableFilterControl({
  filter,
  value,
  processing,
  bare = false,
  onChange,
  onSearch,
}: {
  filter: FilterNode;
  value: unknown;
  processing: boolean;
  bare?: boolean;
  onChange: (value: unknown) => void;
  onSearch?: FilterOptionSearch;
}) {
  const { t } = useT("lattice");
  const schema = filter.schema ?? [];

  if (schema.length === 0) {
    return (
      <ToggleControl filter={filter} value={value} processing={processing} onChange={onChange} />
    );
  }

  return (
    <div className="flex items-end gap-2">
      <div className="min-w-0 flex-1">
        <SchemaControl
          filter={filter}
          schema={schema}
          value={value}
          processing={processing}
          bare={bare}
          onChange={onChange}
          onSearch={onSearch}
        />
      </div>
      {isActiveFilterValue(value) && (
        <IconButton
          size="md"
          icon="x"
          label={t("table.filter.clear", "Clear {{label}} filter", {
            label: filter.props.label,
          })}
          disabled={processing}
          onClick={() => onChange(undefined)}
        />
      )}
    </div>
  );
}

function SchemaControl({
  filter,
  schema,
  value,
  processing,
  bare,
  onChange,
  onSearch,
}: {
  filter: FilterNode;
  schema: Node[];
  value: unknown;
  processing: boolean;
  bare: boolean;
  onChange: (value: unknown) => void;
  onSearch?: FilterOptionSearch;
}) {
  const initial = useMemo(() => filterValue(value), [value]);
  const form = useMemo(
    () => ({
      action: "#",
      clearErrors: () => {},
      componentId: `table-filter-${filter.key}`,
      componentRef: "",
      errors: {},
      fieldIdPrefix: `table-filter-${filter.key}`,
      fieldLabels: {},
      precognitive: false,
      processing,
      searchOptions: (
        field: string,
        query: string,
        _values: Record<string, unknown>,
        signal: AbortSignal,
      ) => (onSearch ? onSearch(field, query, signal) : Promise.resolve([])),
      touch: () => {},
      validate: () => {},
      validateFields: () => {},
      validating: false,
    }),
    [filter.key, onSearch, processing],
  );

  const content = (
    <div
      aria-disabled={processing}
      className={cn("grid gap-3", processing && "pointer-events-none opacity-60")}
    >
      <Renderer nodes={schema} />
    </div>
  );

  return (
    <FormProvider value={form}>
      <PrefillProvider value={{ markUserEdit: () => {} }}>
        <ResolvedNodesProvider nodes={{}}>
          <FormValuesProvider initial={initial}>
            <TableFilterCommitBridge onChange={onChange}>
              {bare ? <TableCellProvider>{content}</TableCellProvider> : content}
            </TableFilterCommitBridge>
          </FormValuesProvider>
        </ResolvedNodesProvider>
      </PrefillProvider>
    </FormProvider>
  );
}

function TableFilterCommitBridge({
  children,
  onChange,
}: {
  children: ReactNode;
  onChange: (value: FilterValue) => void;
}) {
  const values = useFormValues();
  const setValue = useSetFormValue();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const write = useCallback(
    (name: string, value: unknown) => {
      const nextValue =
        typeof value === "function"
          ? (value as (previous: unknown) => unknown)(getPath(valuesRef.current, name))
          : value;
      const next = setPath(valuesRef.current, name, nextValue);

      valuesRef.current = next;
      setValue(name, nextValue);
      onChange(next);
    },
    [onChange, setValue],
  );

  const commit = useMemo(
    () => ({
      blur: () => {},
      change: write,
      commit: write,
    }),
    [write],
  );

  return <FieldCommitOverrideProvider value={commit}>{children}</FieldCommitOverrideProvider>;
}

function ToggleControl({
  filter,
  value,
  processing,
  onChange,
}: {
  filter: FilterNode;
  value: unknown;
  processing: boolean;
  onChange: (value: unknown) => void;
}) {
  const checked = isTruthy(filterValue(value).value);

  return (
    <label className="flex h-lt-control-md cursor-pointer items-center gap-2 text-sm">
      <Checkbox
        aria-label={filter.props.label}
        data-test={`table-filter-${filter.key}`}
        checked={checked}
        disabled={processing}
        onCheckedChange={(next) => onChange(next === true ? { value: "1" } : undefined)}
      />
      <span>{filter.props.label}</span>
    </label>
  );
}
